import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Track } from '@app/core/models/Track';
import { LogModal } from '@app/core/states/events-tracking.actions';
import { RadioState } from '@app/core/states/radio.state';
import { aLogEvent, AMPLITUDE_EVENT } from '@app/shared/amplitude-events';
import { SavedToLocalstorage } from '@app/shared/constants';
import { timeRangeValidator, weekDaysValidator } from '@app/shared/custom-validators';
import { EventApi } from '@fullcalendar/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { SchedulerTrack } from '@radioking/scheduler-core';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { SugestionInput } from '@radioking/ui/input';
import { Weekdays, WeekdaySelectValue } from '@radioking/ui/weekday-select';
import moment, { isMoment } from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, startWith, take } from 'rxjs/operators';
import { TooltipMagicIcon } from './../../states/planning.actions';

import { MatStepper } from '@angular/material/stepper';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { PopoverProps } from 'libs/ui/video-popover/video-popover.directive';
import { ProgramsState } from '../../../../../programs/src/lib/states/programs.state';
import { SchedulerCoreState } from '../../../../../scheduler-core/src/lib/states/scheduler-core.state';
import { InlayType, Slot, SlotKind, SlotPlayType } from '../../models/planning.model';
import { PlanningUtilsService } from '../../services/planning-utils.service';
import {
  NoContentSelected,
  PlanningCreateSlotFailure,
  PlanningCreateSlotRequest,
  PlanningCreateSlotSuccess,
  PlanningEditSlotFailure,
  PlanningEditSlotRequest,
  PlanningEditSlotSuccess,
  SlotOverrideExisting,
} from '../../states/planning.actions';

interface SlotEditionFormValues {
  program?: SugestionInput;
  playlist?: SugestionInput;
  playType: SlotPlayType;
  dateDay: { startDate: moment.Moment; endDate: moment.Moment };
  dateStart: string;
  dateEnd: string;
  fixedTime: boolean;
  tolerance?: number;
  repeat: boolean;
  weekdays: WeekdaySelectValue;
  frequency: SugestionInput;
  repeatEnd?: { startDate: moment.Moment; endDate: moment.Moment };
  repeatChoice: string;
  repeatOccurrences: number;
  inlay: boolean;
  inlayType: InlayType;
  inlayTrack?: Track | SchedulerTrack;
  inlayPlaylist?: SugestionInput;
  inlayPlaylistTracks?: number;
  inlayRepeat?: number;
}

/**
 * Form default values
 */
const formDefaultValues: SlotEditionFormValues = {
  program: null,
  playlist: null,
  playType: 'sequential',
  dateDay: null,
  dateStart: null,
  dateEnd: null,
  fixedTime: false,
  tolerance: 5,
  repeat: false,
  weekdays: null,
  frequency: PlanningUtilsService.slotRepeatFrequency[0],
  repeatChoice: 'date',
  repeatOccurrences: 1,
  inlay: false,
  inlayType: 'track',
  inlayTrack: null,
  inlayPlaylist: null,
  inlayPlaylistTracks: 1,
  inlayRepeat: 5,
};

@Component({
  selector: 'rk-planning-slot-edition',
  templateUrl: './planning-slot-edition.component.html',
  styleUrls: ['./planning-slot-edition.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanningSlotEditionComponent implements OnInit, OnDestroy {
  /**
   * Edition or creation mode
   *
   * See {@link initializeEditionMode}
   */
  isEdition = false;

  /**
   * Active tab (defaults to "playlist" tab)
   *
   * See {@link initializeTabs}
   * See {@link changeActiveTab}
   */
  activeTab: SlotKind;

  frequencyList = PlanningUtilsService.slotRepeatFrequency;

  /**
   * Form instance
   */
  form: FormGroup;

  radioTz: string;

  /**
   * Slot min date - today
   */
  minDate = moment().startOf('day');

  /**
   * Repeat max date limit - 6 month from slot start date
   */
  repeatMaxDate: moment.Moment = null;

  /**
   * Enums exposed to template
   */
  enums = { SlotKind };

  /**
   * Whether program choices for dropdown are empty or not
   */
  @Select(ProgramsState.hasNotEmptyPrograms)
  hasPrograms$: Observable<boolean>;

  /**
   * Program choices for dropdown
   */
  @Select(ProgramsState.allNotEmptyProgramsForDropdown)
  allPrograms$: Observable<SugestionInput[]>;

  /**
   * Whether playlist choices for dropdown are empty or not
   */
  @Select(SchedulerCoreState.hasPlaylists)
  hasPlaylists$: Observable<boolean>;

  /**
   * Playlist choices for dropdown
   */
  @Select(SchedulerCoreState.allPlaylistsForDropdown)
  allPlaylists$: Observable<SugestionInput[]>;

  @Select(SchedulerCoreState.allTracks)
  allTracks$: Observable<SchedulerTrack[]>;

  /**
   * Formatted event/slot date for its form field
   *
   * See {@link formatSlotDate}
   */
  formattedSlotDate = '';

  formattedRepeatDate = '';

  /**
   * Whether tolerance field is visible or not
   *
   * See {@link toggleToleranceField}
   */
  isToleranceVisible = false;
  isRepeatActive = false;
  isDaysVisible = true;
  isInlayActive = false;
  durationEdited = false;

  /**
   * Whether dialog is in a loading state or not (when saving)
   */
  isLoading$: Observable<boolean>;

  subscription = new Subscription();

  planningHintKey = SavedToLocalstorage.PLANNING_6_MONTH_HINT;

  popover: PopoverProps = {
    title: marker('VIDEO_TUTORIAL.PLANNING_TITLE'),
    description: marker('VIDEO_TUTORIAL.PLANNING_DESCRIPTION'),
    thumb: 'planning.jpg',
    src: marker('VIDEO_TUTORIAL.VIDEO_PLANNING_URL'),
    knowledgeBaseUrl: marker('VIDEO_TUTORIAL.TUTO_PLANNING_URL'),
  };

  get mobile(): boolean {
    return this.data.mobile;
  }

  get event(): Partial<EventApi> {
    return this.data.event;
  }

  /* Variables needed for mobile */
  $step = signal(0);
  @ViewChild('stepper')
  stepper: MatStepper;
  searchSubject = new BehaviorSubject<string>('');
  $filteredPrograms: WritableSignal<SugestionInput[]> = signal([]);
  $filteredPlaylists: WritableSignal<SugestionInput[]> = signal([]);
  $seeAllPrograms = signal(false);
  $seeAllPlaylists = signal(false);

  constructor(
    private readonly actions$: Actions,
    private readonly uiStore: UiStoreHelperService,
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<PlanningSlotEditionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { event: Partial<EventApi>; mobile?: boolean },
  ) {
    this.radioTz = this.store.selectSnapshot(RadioState.currentRadioTimezone);
    this.activeTab = this.store.selectSnapshot(SchedulerCoreState.hasPlaylists)
      ? SlotKind.playlist
      : SlotKind.program;
  }

  ngOnInit(): void {
    this.initializeEditionMode();
    this.initializeTabs();
    this.initializeForm();
    if (!this.isEdition) {
      this.setDurationToPlaylistTime();
    }

    this.isLoading$ = this.uiStore.getLoadingState(
      [PlanningCreateSlotRequest, PlanningEditSlotRequest],
      [
        PlanningCreateSlotFailure,
        PlanningCreateSlotSuccess,
        PlanningEditSlotFailure,
        PlanningEditSlotSuccess,
        SlotOverrideExisting,
      ],
    );

    // Creation/edition succeed: close this dialog
    this.subscription.add(
      this.actions$
        .pipe(ofActionDispatched(PlanningCreateSlotSuccess, PlanningEditSlotSuccess))
        .subscribe(() => {
          this.closeSelf();
        }),
    );
    this.logModal();

    if (this.mobile) {
      this.subscription.add(
        combineLatest([
          this.allPrograms$,
          this.allPlaylists$,
          this.allTracks$,
          this.searchSubject.asObservable(),
        ]).subscribe(([allPrograms, playlists, tracks, search]) => {
          this.$filteredPrograms.set(
            search.length < 1
              ? allPrograms
              : allPrograms.filter(program =>
                  program.name.toLowerCase().includes(search.toLowerCase()),
                ),
          );
          this.$filteredPlaylists.set(
            search.length < 1
              ? playlists
              : playlists.filter(playlist =>
                  playlist.name.toLowerCase().includes(search.toLowerCase()),
                ),
          );
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Determine whether we're in edition or creation mode
   */
  initializeEditionMode(): void {
    this.isEdition = Boolean(this.event.extendedProps?.id);
    this.durationEdited = this.isEdition;
  }

  /**
   * Determine initial active tab
   */
  initializeTabs(): void {
    this.activeTab = this.event.extendedProps?.kind ?? this.activeTab;
    aLogEvent(
      this.activeTab === 'playlist'
        ? AMPLITUDE_EVENT.PLANNING.PLAYLIST
        : AMPLITUDE_EVENT.PLANNING.PROGRAM,
    );
  }

  /**
   * Initialize form (values, events)
   */
  initializeForm(): void {
    const formValues = this.eventToFormValues();
    this.form = this.fb.group({
      program: formValues.program,
      playlist: formValues.playlist,
      playType: formValues.playType,
      dateDay: formValues.dateDay,
      dateStart: [formValues.dateStart, timeRangeValidator('dateEnd', true)],
      dateEnd: [formValues.dateEnd, timeRangeValidator('dateStart', false)],
      fixedTime: formValues.fixedTime,
      tolerance: [formValues.tolerance, Validators.min(0)],
      repeat: [formValues.repeat, weekDaysValidator()],
      weekdays: [formValues.weekdays, weekDaysValidator(false)],
      frequency: [formValues.frequency, weekDaysValidator(false)],
      repeatEnd: formValues.repeatEnd,
      repeatChoice: formValues.repeatChoice,
      repeatOccurrences: formValues.repeatOccurrences,
      inlay: formValues.inlay,
      inlayType: formValues.inlayType,
      inlayPlaylist: formValues.inlayPlaylist,
      inlayTrack: formValues.inlayTrack,
      inlayPlaylistTracks: formValues.inlayPlaylistTracks,
      inlayRepeat: formValues.inlayRepeat,
    });

    // Default value for "program" field
    this.subscription.add(
      this.allPrograms$
        .pipe(
          filter(v => !!v && this.form.get('program').value === null && v.length > 0),
          filter(() => !this.mobile || (this.mobile && !this.isEdition)),
          take(1),
        )
        .subscribe(programs => {
          this.form.get('program').setValue(programs[0]);
          if (this.mobile) {
            this.activeTab = SlotKind.program;
          }
        }),
    );

    // Default value for "playlist" field
    this.subscription.add(
      this.allPlaylists$
        .pipe(
          filter(v => !!v && this.form.get('playlist').value === null && v.length > 0),
          filter(() => !this.mobile || (this.mobile && !this.isEdition)),
          take(1),
        )
        .subscribe(playlists => {
          if (!this.mobile || !this.form.get('program').value) {
            this.form.get('playlist').setValue(playlists[0]);
          }
          const inlayPlaylist = this.form.get('inlayPlaylist');
          if (!inlayPlaylist.value) {
            inlayPlaylist.setValue(playlists[0]);
          }
        }),
    );

    this.subscription.add(
      this.form
        .get('fixedTime')
        .valueChanges.pipe(startWith(formValues.fixedTime))
        .subscribe(fixedTime => this.toggleToleranceField(fixedTime)),
    );

    this.subscription.add(
      this.form
        .get('playType')
        .valueChanges.pipe(startWith(formValues.playType))
        .subscribe(playType => {
          aLogEvent(
            playType === 'sequential'
              ? AMPLITUDE_EVENT.PLANNING.BROADCAST_SEQUENTIAL
              : AMPLITUDE_EVENT.PLANNING.BROADCAST_RANDOM,
          );
        }),
    );

    this.subscription.add(
      this.form
        .get('repeat')
        .valueChanges.pipe(startWith(formValues.repeat))
        .subscribe(repeat => this.toggleRepeat(repeat)),
    );

    this.subscription.add(
      this.form
        .get('frequency')
        .valueChanges.pipe(startWith(formValues.frequency), distinctUntilChanged())
        .subscribe(frequency => this.toggleDays(frequency)),
    );

    this.subscription.add(
      this.form
        .get('repeatChoice')
        .valueChanges.pipe(startWith(formValues.repeatChoice), distinctUntilChanged())
        .subscribe(repeatChoice => {
          if (this.form.get('repeat') && this.form.get('repeat').value)
            if (repeatChoice === 'occurrences') {
              aLogEvent(AMPLITUDE_EVENT.PLANNING.ENDS_NUMBER_REPETITIONS);
            }
        }),
    );

    this.subscription.add(
      this.form
        .get('inlay')
        .valueChanges.pipe(startWith(formValues.inlay))
        .subscribe(inlay => this.toggleInlay(inlay)),
    );

    this.subscription.add(
      this.form
        .get('playlist')
        .valueChanges.pipe(
          startWith(formValues.playlist),
          filter(p => !!p),
        )
        .pipe(delay(50)) // Delay to let the form SloKind update
        .subscribe(() => {
          if (!this.durationEdited) {
            this.setDurationToPlaylistTime();
          }
        }),
    );

    this.subscription.add(
      this.form
        .get('dateStart')
        .valueChanges.pipe(
          filter(() => !this.isEdition && this.form.get('dateEnd').pristine),
        )
        .subscribe(dateStart => {
          if (this.activeTab === 'playlist') {
            this.setDurationToPlaylistTime();
          } else {
            const start = moment(dateStart, 'HH:mm');
            const computedEnd = start.clone().add(2, 'hour');

            PlanningUtilsService.restrictStartEndDatesToSameDay(start, computedEnd);
            this.form.get('dateEnd').setValue(computedEnd.format('HH:mm'));
          }
        }),
    );
  }

  changeDuration() {
    if (this.form.get('dateEnd').dirty) {
      this.durationEdited = true;
    }
  }

  /**
   * Convert an event/slot to form values
   */
  eventToFormValues(): SlotEditionFormValues {
    const programs = this.store.selectSnapshot(ProgramsState.allProgramsForDropdown);
    const playlists = this.store.selectSnapshot(
      SchedulerCoreState.allPlaylistsForDropdown,
    );
    const program = this.event.extendedProps?.program
      ? programs.find(v => v.id === this.event.extendedProps.program)
      : formDefaultValues.program;
    const playlist = this.event.extendedProps?.playlist
      ? playlists.find(v => v.id === this.event.extendedProps.playlist)
      : formDefaultValues.playlist;
    const playType = this.event.extendedProps?.playType ?? formDefaultValues.playType;
    const startDay = (
      this.isEdition ? this.event.extendedProps.start : moment(this.event.start)
    ).tz(this.radioTz);
    const startHour = moment(this.event.start).tz(this.radioTz);
    const endHour = this.event.end ? moment(this.event.end).tz(this.radioTz) : null;

    if (!this.isEdition && startHour && endHour) {
      PlanningUtilsService.restrictStartEndDatesToSameDay(startHour, endHour);
    }

    this.formatSlotDate(startDay);

    const dateDay = startDay
      ? {
          startDate: startDay.clone(),
          endDate: startDay.clone(), // Same as startDate because we don't want a range
        }
      : formDefaultValues.dateDay;
    this.repeatMaxDate = dateDay.startDate.clone().add(6, 'month');
    const dateStart = startHour ? startHour.format('HH:mm') : formDefaultValues.dateStart;
    const dateEnd = endHour ? endHour.format('HH:mm') : formDefaultValues.dateEnd;
    const fixedTime = this.event.extendedProps?.fixedTime ?? formDefaultValues.fixedTime;
    const tolerance = this.event.extendedProps?.tolerance ?? formDefaultValues.tolerance;

    // Repeat fields
    const repeat = this.event.extendedProps?.repeat?.repeatInterval
      ? true
      : formDefaultValues.repeat;

    let weekdays: WeekdaySelectValue = formDefaultValues.weekdays;
    let frequency = formDefaultValues.frequency;
    let repeatChoice = formDefaultValues.repeatChoice;
    let repeatEndDate = startDay.clone().add(7, 'days');
    let repeatOccurrences = formDefaultValues.repeatOccurrences;
    if (repeat) {
      const eventR = this.event.extendedProps.repeat;
      if (eventR.repeatDays) {
        weekdays = new Set<Weekdays>(eventR.repeatDays.toString().split(','));
      }

      const frequencyVal = `${eventR.repeatInterval ?? 0}`;
      if (frequencyVal !== '0') {
        frequency = this.frequencyList.find(f => f.id === frequencyVal);
      }
      if (eventR.repeatCount) {
        repeatChoice = eventR.repeatCount ? 'occurrences' : 'date';
      }
      if (eventR.repeatEnd) {
        repeatEndDate = this.event.extendedProps?.repeat?.repeatEnd;
      }
      if (eventR.repeatCount) {
        repeatOccurrences = eventR.repeatCount;
      }
    }

    const repeatEnd = {
      startDate: repeatEndDate.clone(),
      endDate: repeatEndDate.clone(), // Same as startDate because we don't want a range
    };
    this.formatRepeatDate(repeatEndDate);

    // Inlay fields
    const inlay = this.event.extendedProps?.inlay ?? formDefaultValues.inlay;
    let inlayType = formDefaultValues.inlayType;
    let inlayTrack: SchedulerTrack = null;
    let inlayPlaylist: SugestionInput = null;
    let inlayPlaylistTracks = formDefaultValues.inlayPlaylistTracks;
    let inlayRepeat = formDefaultValues.inlayRepeat;
    if (inlay) {
      const eventI = this.event.extendedProps.inlay;
      inlayType = eventI.type;
      const inlayTrackId =
        inlayType === 'track' ? eventI.id : formDefaultValues.inlayTrack;
      if (inlayTrackId) {
        inlayTrack = this.store
          .selectSnapshot(SchedulerCoreState.allTracks)
          .find(t => t.id === inlayTrackId);
      }
      const inlayPlaylistId =
        inlayType === 'playlist' ? eventI.id : formDefaultValues.inlayPlaylist;
      if (inlayPlaylistId) {
        inlayPlaylist = playlists.find(v => v.id === inlayPlaylistId);
      }
      if (eventI.playlistTracks) {
        inlayPlaylistTracks = eventI.playlistTracks;
      }
      if (eventI.interval) {
        inlayRepeat = eventI.interval;
      }
    }

    return {
      program,
      playlist,
      playType,
      dateDay,
      dateStart,
      dateEnd,
      fixedTime,
      tolerance,
      repeat,
      frequency,
      weekdays,
      repeatChoice,
      repeatEnd,
      repeatOccurrences,
      inlay,
      inlayType,
      inlayTrack,
      inlayPlaylist,
      inlayPlaylistTracks,
      inlayRepeat,
    };
  }

  /**
   * Change active tab to specified one
   */
  changeActiveTab(e: MatButtonToggleChange): void {
    aLogEvent(
      e.value === 'playlist'
        ? AMPLITUDE_EVENT.PLANNING.PLAYLIST
        : AMPLITUDE_EVENT.PLANNING.PROGRAM,
    );
    this.activeTab = e.value;
  }

  /**
   * Event/slot's date changed
   */
  slotDateChanged(event: any): void {
    this.repeatMaxDate = event.startDate.clone().add(6, 'month');
    if (this.form.get('repeatEnd').value.startDate.isAfter(this.repeatMaxDate)) {
      this.form.get('repeatEnd').setValue({
        startDate: this.repeatMaxDate.clone(),
        endDate: this.repeatMaxDate.clone(),
      });
      this.formatRepeatDate(this.repeatMaxDate);
    }
    this.formatSlotDate(event.startDate);
  }

  repeatDateChanged(event: any): void {
    aLogEvent(AMPLITUDE_EVENT.PLANNING.ENDS_DATE);
    this.formatRepeatDate(event.startDate);
  }

  /**
   * Compute start/end dates automatically when requested
   *
   * Starts on midnight when empty
   * Lasts 1 hour
   */
  setDurationToPlaylistTime(manual = false, tooltip = false): void {
    if (manual) {
      this.durationEdited = false;
      this.form.get('dateEnd').markAsPristine();
    }
    if (this.activeTab === this.enums.SlotKind.program) {
      return;
    }
    if (tooltip) {
      this.tooltipMagicIcon();
    }
    const startField = this.form.get('dateStart');
    const endField = this.form.get('dateEnd');

    // Default to 2 hours if playlist is empty
    const duration = this.form.get('playlist').value.extra.duration || 7200;

    if (!startField.value) {
      startField.setValue('00:00');
    }

    const start = moment(startField.value, 'HH:mm');
    const computedEnd = start.clone().add(Math.max(duration, 60), 'seconds');

    PlanningUtilsService.restrictStartEndDatesToSameDay(start, computedEnd);
    endField.setValue(computedEnd.format('HH:mm'));
  }

  /**
   * Toggle "tolerance" field depending on "fixedTime" field
   *
   * It must be visible when "fixedTimed" is checked, hidden otherwise
   */
  toggleToleranceField(fixedTime: boolean): void {
    this.isToleranceVisible = fixedTime;

    if (!this.isToleranceVisible) {
      // Prevent a hidden field to invalidate form
      this.form.get('tolerance').setValue(formDefaultValues.tolerance);
    } else {
      aLogEvent(AMPLITUDE_EVENT.PLANNING.BROADCAST_FIXEDTIME);
    }
  }

  toggleRepeat(repeat: boolean): void {
    if (!this.isRepeatActive && repeat) {
      aLogEvent(AMPLITUDE_EVENT.PLANNING.REPETITION_ACTIVE);
      if (this.form.get('weekdays').pristine && !this.isEdition) {
        this.form
          .get('weekdays')
          .setValue(new Set([this.form.get('dateDay').value.startDate.day().toString()]));
      }
    }
    this.isRepeatActive = repeat;
  }

  toggleDays(days: SugestionInput) {
    if (this.form.get('repeat').value) {
      let logEvent = AMPLITUDE_EVENT.PLANNING.REPETITION_EVERYWEEK;
      if (days.id === '1') {
        logEvent = AMPLITUDE_EVENT.PLANNING.REPETITION_EVERYDAY;
      }
      if (days.id === '14') {
        logEvent = AMPLITUDE_EVENT.PLANNING.REPETITION_EVERY2WEEKS;
      }
      aLogEvent(logEvent);
    }

    this.isDaysVisible = days.id !== '1';
  }

  toggleInlay(inlay: boolean): void {
    this.isInlayActive = inlay;
    if (inlay) {
      aLogEvent(AMPLITUDE_EVENT.PLANNING.INCRUSTATION_ACTIVE);
    }
  }

  /**
   * "Save" button clicked
   */
  saveClicked(): void {
    const slot = this.formValuesToSlot();

    if (this.isEdition) {
      this.store.dispatch(new PlanningEditSlotRequest(slot));
    } else {
      this.store.dispatch(new PlanningCreateSlotRequest(slot));
    }
  }

  /**
   * Convert form values to an event/slot
   */
  formValuesToSlot(): Slot {
    const radioTz = this.store.selectSnapshot(RadioState.currentRadioTimezone);
    const formValues: SlotEditionFormValues = this.form.getRawValue();
    const dateDay = formValues.dateDay.startDate.tz(radioTz, true);
    const dateStart = moment(formValues.dateStart, 'HH:mm');
    const dateEnd = moment(formValues.dateEnd, 'HH:mm');
    const slot: Slot = {
      kind: this.activeTab,
      playType: formValues.playType,
      start: dateDay.clone().set({
        hours: dateStart.hours(),
        minutes: dateStart.minutes(),
      }),
      end: dateDay.clone().set({
        hours: dateEnd.hours(),
        minutes: dateEnd.minutes(),
      }),
      fixedTime: formValues.fixedTime,
      occurrences: null,
    };
    slot.startHour = slot.start;
    slot.endHour = slot.end;
    if (formValues.repeat) {
      const interval = Number.parseInt(formValues.frequency.id, 10);
      slot.repeat = {
        repeatCount:
          formValues.repeatChoice === 'occurrences' ? formValues.repeatOccurrences : null,
        repeatInterval: interval,
        repeatEnd:
          formValues.repeatChoice === 'date'
            ? moment(
                isMoment(formValues.repeatEnd)
                  ? formValues.repeatEnd
                  : formValues.repeatEnd.startDate,
              ).startOf('day')
            : null,
        repeatDays:
          interval > 1 && formValues.weekdays
            ? Array.from(formValues.weekdays).join(',')
            : null,
      };
    }
    if (
      formValues.inlay &&
      (formValues.inlayType === 'playlist' ||
        (formValues.inlayType === 'track' && formValues.inlayTrack?.id))
    ) {
      slot.inlay = {
        id:
          formValues.inlayType === 'track'
            ? formValues.inlayTrack.id
            : formValues.inlayPlaylist.id,
        type: formValues.inlayType,
        interval: formValues.inlayRepeat,
        playlistTracks:
          formValues.inlayType === 'playlist' ? formValues.inlayPlaylistTracks : 0,
      };
    }

    if (this.isEdition) {
      slot.id = this.event.extendedProps.id;
    }

    if (this.activeTab === SlotKind.program) {
      slot.program = formValues.program.id;
    } else if (this.activeTab === SlotKind.playlist) {
      slot.playlist = formValues.playlist.id;
    }

    if (formValues.fixedTime) {
      slot.tolerance = formValues.tolerance ?? formDefaultValues.tolerance;
    }

    return slot;
  }

  setInlayTrack(track: Track) {
    this.form.get('inlayTrack').setValue(track);
  }

  /**
   * Close self (closing dialog)
   */
  closeSelf(): void {
    this.dialogRef.close();
  }

  /**
   * Format event/slot date
   */
  formatSlotDate(slotDate: moment.Moment): void {
    const desiredFormat = moment.localeData().longDateFormat('ll');
    this.formattedSlotDate = slotDate.format(desiredFormat);
  }

  formatRepeatDate(date: moment.Moment): void {
    const desiredFormat = moment.localeData().longDateFormat('ll');
    this.formattedRepeatDate = date.format(desiredFormat);
  }

  clickInlayChoice(choice = 'track') {
    aLogEvent(
      choice === 'track'
        ? AMPLITUDE_EVENT.PLANNING.INCRUSTATION_TRACK
        : AMPLITUDE_EVENT.PLANNING.INCRUSTATION_PLAYLIST,
    );
  }

  clickInlayPlaylist(field = 'list') {
    const logEvent =
      field === 'list'
        ? AMPLITUDE_EVENT.PLANNING.INCRUSTATION_PLAYLIST_LIST
        : field === 'number'
          ? AMPLITUDE_EVENT.PLANNING.INCRUSTATION_PLAYLIST_NUMBER
          : AMPLITUDE_EVENT.PLANNING.INCRUSTATION_REPEAT;
    aLogEvent(logEvent);
  }

  showHint() {
    return !localStorage.getItem(this.planningHintKey);
  }

  dismiss6MonthHint() {
    localStorage.setItem(this.planningHintKey, '1');
  }

  @Dispatch()
  logModal = () =>
    new LogModal({ name: AMPLITUDE_EVENT.MODAL.ADD_EDIT_PLANNING, origin: 'Planning' });

  /* Functions needed for mobile */
  nextStep() {
    if (
      this.$step() === 0 &&
      this.form.get('program').value === null &&
      this.form.get('playlist').value === null &&
      this.form.get('track').value === null
    ) {
      this.noContentSelected();
      return;
    }
    this.$step.update(v => v + 1);
    this.stepper.next();
  }

  prevStep() {
    this.$step.update(v => v - 1);
    this.stepper.previous();
  }

  toggleAllPrograms() {
    this.$seeAllPrograms.update(v => !v);
  }

  toggleAllPlaylists() {
    this.$seeAllPlaylists.update(v => !v);
  }

  editSearch(e: string) {
    if (typeof e === 'string') {
      this.searchSubject.next(e);
    }
  }

  selectProgram(program: SugestionInput) {
    this.form.get('program').setValue(program);
    this.form.get('playlist').setValue(null);
    this.activeTab = SlotKind.program;
  }

  selectPlaylist(playlist: SugestionInput) {
    this.form.get('playlist').setValue(playlist);
    this.form.get('program').setValue(null);
    this.activeTab = SlotKind.playlist;
  }

  restrictedProgramList() {
    if (this.$seeAllPrograms()) {
      return this.$filteredPrograms();
    }
    if (this.form.get('program').value) {
      const programIndex = this.$filteredPrograms().findIndex(
        p => p.id === this.form.get('program').value.id,
      );
      if (programIndex > 3) {
        return [
          ...this.$filteredPrograms().slice(0, 3),
          this.$filteredPrograms()[programIndex],
        ];
      }
    }
    return this.$filteredPrograms().slice(0, 4);
  }

  restrictedPlaylistList() {
    if (this.$seeAllPlaylists()) {
      return this.$filteredPlaylists();
    }
    if (this.form.get('playlist').value) {
      const playlistIndex = this.$filteredPlaylists().findIndex(
        p => p.id === this.form.get('playlist').value.id,
      );
      if (playlistIndex > 3) {
        return [
          ...this.$filteredPlaylists().slice(0, 3),
          this.$filteredPlaylists()[playlistIndex],
        ];
      }
    }
    return this.$filteredPlaylists().slice(0, 4);
  }

  @Dispatch()
  noContentSelected = () => new NoContentSelected();

  @Dispatch()
  tooltipMagicIcon = () => new TooltipMagicIcon();
}
